<template>
  <div>
    <div
      @click="showSubscription = true"
      class="card stage-card"
      v-bind:class="remainingCapacity > 0 ? 'is-capacity-left' : ''"
    >
      <header class="card-header">
        <p class="card-header-title">
          <b-icon
            v-if="stage.is_in_restrictive_registration_period && haveUnRegistrableCustomers"
            size="is-small"
            icon="lock"
            style="color: #ef5350"
          />
          {{ timeTitle }}<br>
          <small class="has-text-weight-normal has-text-grey">Première séance le {{ stage.start_date | dateFormat }}</small>
        </p>
      </header>
      <div class="card-content pb-3 pt-3">
        <p class="text-center has-text-weight-bold">
          {{ age }} -
          <span>
            <b-icon size="is-small" icon="circle" :style="`font-size: 0.8em; color: ${stage.level.color_code}`" />
            {{ stage.level.name }}
          </span>
        </p>
        <p class="text-center mt-2 mb-2">
          <span
            v-if="remainingCapacity > 0"
            class="tag is-medium is-rounded"
          >
            {{ remainingCapacity }} places restantes
          </span>
          <span
            v-else
            class="tag is-medium is-rounded has-text-danger"
          >
            Complet
          </span>
        </p>
        <p class="text-center">
          {{ stage.number_of_lesson }} séances
        </p>
        <div
          v-if="customersByPlannedStages[`planned-stage-${stage.id}`]"
          class="text-center mt-3 stage-card-customers-tag"
        >
          <b-tag
            v-for="(customer, index) in customersByPlannedStages[`planned-stage-${stage.id}`].customers"
            :key="`customer-index-${index}`"
            type="is-success is-light"
            size="is-medium"
            class="ml-1 mr-1"
            rounded
            closable
            :aria-close-label="`Retirer ${customer.firstname} de ce cours`"
            @close.stop="removeFromTag(customer)"
          >
            {{ customer.firstname }} {{ customer.lastname }}
          </b-tag>
        </div>
      </div>
    </div>

    <b-modal v-model="showSubscription">
      <div class="card">
        <header class="card-header">
          <p class="card-header-title">
            Qui voulez-vous inscrire à ce cours ?
          </p>
        </header>
        <section class="card-content">
          <b-message
            v-if="stage.is_in_restrictive_registration_period"
            type="is-warning"
          >
            Inscription réservée aux élèves de l'année passée jusqu'au {{ stage.restrictive_registration_date | dateFormat("DD MMMM YYYY") }}.
          </b-message>
          <b-message
            v-if="haveToYoungChild"
            type="is-warning"
          >
            Les participants à ce cours doivent avoir {{ stage.min_age }} ans révolu au {{ stage.start_date | dateFormat("DD MMMM YYYY") }}
          </b-message>
          <p class="has-text-weight-bold">
            <b-icon size="is-small" icon="circle" :style="`font-size: 0.8em; color: ${stage.level.color_code}`" /> {{ stage.level.name }} {{ age }}, {{ timeTitle.toLowerCase() }}
          </p>
          <p class="mb-2">
            {{ stage.number_of_lesson }} séances du {{ stage.start_date | dateFormat("DD MMMM YYYY") }} au {{ stage.end_date | dateFormat("DD MMMM YYYY") }}
          </p>
          <div>
            <p class="mb-4">
              Place restante : <strong>{{ remainingCapacity }}</strong>
            </p>
            <div
              class="mt-2"
              v-for="(customer, index) in customers"
              :key="`customer-index-${index}`"
            >
              <b-checkbox
                v-model="customersGroup"
                :native-value="customer.customerIndex"
                :disabled="!canBeSubscribed(customer) || ((remainingCapacity - customersGroup.length) <= 0 && !customersGroup.includes(customer.customerIndex))"
              >
                {{ customer.firstname }} {{ customer.lastname }}
                <br v-if="customer.level.id !== stage.level.id && canBeSubscribed(customer)">
                <small
                  v-if="customer.level.id !== stage.level.id && canBeSubscribed(customer)"
                  class="has-text-grey"
                >
                  {{ customer.firstname }} a le niveau "{{ customer.level.name }}", mais il/elle peut s'inscrire à un cour de niveau "{{ stage.level.name }}".
                </small>
              </b-checkbox>
            </div>
          </div>
        </section>
        <footer class="card-footer">
          <a
            class="card-footer-item"
            @click="sendCustomers"
          >
            <span v-if="customersGroup.length > 0">
              Valider
            </span>
            <span v-else>
              Fermer
            </span>
          </a>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { DateHelpers } from "@/mixins/DateHelpers";
import moment from "moment";

export default {
  name: "StageCard",
  mixins: [DateHelpers],

  props: {
    stage: {
      type: Object,
      required: true
    },
    dayName: {
      type: String,
      required: true
    },
    customers: {
      type: Array,
      required: true
    },
    addStageToCustomer: {
      type: Function,
      required: true
    },
    removeStageToCustomer: {
      type: Function,
      required: true
    },
    customersByPlannedStages: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      showSubscription: false,
      customersGroup: []
    }
  },

  computed: {
    timeTitle () {
      return `Le ${this.dayName} de ${moment(this.stage.start_time).format('HH:mm')} à ${moment(this.stage.end_time).format('HH:mm')}`;
    },

    age () {
      if (this.stage.min_age >= 18) {
        return `Adulte`;
      } else if (this.stage.min_age !== this.stage.max_age) {
        return `${this.stage.min_age}-${this.stage.max_age} ans`;
      } else {
        return `${this.stage.min_age} ans`;
      }
    },

    remainingCapacity () {
      return this.stage.remaining_capacity - this.stage.reservations_in_baskets;
    },

    haveUnRegistrableCustomers () {
      for (const customer of this.customers) {
        if (!customer.can_reregister) {
          return true;
        }
      }
      return false;
    },

    haveToYoungChild () {
      for (const customer of this.customers) {
        if (
          this.stage.registration_age_calculation_method === "from_first_planned_stage_date" &&
          customer.projected_age.age <= this.stage.max_age &&
          customer.projected_age.age >= this.stage.min_age &&
          customer.available_levels.map(level => { return level.id }).includes(this.stage.level.id) &&
          moment(customer.date_of_birth).isAfter(moment(this.stage.minimum_date_of_birth_allowed))
        ) {
          return true;
        }
      }
      return false;
    }
  },

  methods: {
    sendCustomers () {
      // Remove unchecked customer
      if (this.customersByPlannedStages[`planned-stage-${this.stage.id}`]) {
        for (const customerInStage in this.customersByPlannedStages[`planned-stage-${this.stage.id}`].customers) {
          const customer = this.customersByPlannedStages[`planned-stage-${this.stage.id}`].customers[customerInStage]
          if (!this.customersGroup.includes(customer.customerIndex)) {
            this.removeStageToCustomer(
                customer,
                this.stage
            )
          }
        }
      }

      // Add customer in this planned stages
      for (const customerIndex of this.customersGroup) {
        this.addStageToCustomer(
            this.findCustomerByIndex(customerIndex),
            this.stage
        );
      }
      this.showSubscription = false;
    },

    findCustomerByIndex (customerIndex) {
      for (const customer of this.customers) {
        if (customerIndex === customer.customerIndex) {
          return customer;
        }
      }
    },

    canBeSubscribed (customer) {
      if (customer.projected_age.age > this.stage.max_age) { return false }
      if (customer.projected_age.age < this.stage.min_age) { return false }
      if (moment(customer.date_of_birth).isAfter(moment(this.stage.minimum_date_of_birth_allowed))) { return false }
      if (this.stage.is_in_restrictive_registration_period && !customer.can_reregister) { return false }
      if (!customer.available_levels.map(level => { return level.id }).includes(this.stage.level.id)) { return false }

      return true
    },

    removeFromTag (customer) {
      const newCustomersGroup = [];
      for (const customerGroup of this.customersGroup) {
        if (customerGroup !== customer.customerIndex) {
          newCustomersGroup.push(customerGroup)
        }
      }
      this.customersGroup = newCustomersGroup;
      this.sendCustomers();
    }
  }
}
</script>

<style lang="scss">
.stage-card {
  &.is-capacity-left {
    cursor: pointer !important;
    transition: transform 0.1s;
    &:hover {
      transform: scale(102%);
      box-shadow: 0 .5em 1.5em -.125em rgba(10,10,10,.25),0 0 0 1px rgba(10,10,10,.02);
    }
  }
  .stage-card-subscription {
    padding: 1em;
  }
  .card-header-title {
    line-height: 1.25em;
    display: block;
  }
  .stage-card-customers-tag {
    .is-success.is-light {
      background-color: rgb(239, 250, 245);
      color: rgb(37, 121, 83);
      border-style: solid;
      border-width: 1px;
      border-color: rgb(37, 121, 83);
    }
  }
}
</style>
